// Mixpanel is dynamically imported on first user interaction (or after an idle
// fallback) to keep ~169 KB out of the critical bundle. Calls made before
// init are buffered and flushed once the SDK loads.
// Google Analytics is lightweight (just a script tag), but we still defer it
// alongside Mixpanel for consistency.

const GA_MEASUREMENT_ID = "G-LVZ3LETHP8";
const MIXPANEL_TOKEN = "ec17f546f9ceXXXX";

let mixpanelInstance: typeof import("mixpanel-browser").default | null = null;
let mixpanelInitPromise: Promise<void> | null = null;

type QueuedCall =
  | { kind: "track"; eventName: string; properties?: Record<string, any> | undefined }
  | { kind: "identify"; userId: string; userProperties?: Record<string, any> | undefined };

const queue: QueuedCall[] = [];

const flushQueue = () => {
  if (!mixpanelInstance) return;
  while (queue.length) {
    const call = queue.shift()!;
    try {
      if (call.kind === "track") {
        mixpanelInstance.track(call.eventName, call.properties);
      } else {
        mixpanelInstance.identify(call.userId);
        if (call.userProperties) mixpanelInstance.people.set(call.userProperties);
      }
    } catch {
      // no-op
    }
  }
};

// Initialize Google Analytics (lightweight, just a script tag)
export const initGoogleAnalytics = () => {
  if ((window as any).gtag) return;
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  (window as any).dataLayer = (window as any).dataLayer || [];
  function gtag(..._args: any[]) {
    (window as any).dataLayer.push(arguments);
  }
  gtag("js", new Date());
  gtag("config", GA_MEASUREMENT_ID);
  (window as any).gtag = gtag;
};

// Initialize Mixpanel (dynamic import to keep it out of the critical bundle)
export const initMixpanel = () => {
  if (mixpanelInitPromise) return mixpanelInitPromise;
  mixpanelInitPromise = import("mixpanel-browser")
    .then((mod) => {
      mixpanelInstance = mod.default;
      mixpanelInstance.init(MIXPANEL_TOKEN, { track_pageview: true });
      flushQueue();
    })
    .catch((err) => {
      console.warn("[Mixpanel] lazy init failed:", err);
    });
  return mixpanelInitPromise;
};

// Helper to track events across all platforms
export const trackEvent = (eventName: string, properties?: Record<string, any>) => {
  if ((window as any).gtag) {
    (window as any).gtag("event", eventName, properties);
  }
  if (mixpanelInstance) {
    mixpanelInstance.track(eventName, properties);
  } else {
    queue.push({ kind: "track", eventName, properties });
  }
};

// Helper to identify users
export const identifyUser = (userId: string, userProperties?: Record<string, any>) => {
  if ((window as any).gtag) {
    (window as any).gtag("set", { user_id: userId });
  }
  if (mixpanelInstance) {
    mixpanelInstance.identify(userId);
    if (userProperties) mixpanelInstance.people.set(userProperties);
  } else {
    queue.push({ kind: "identify", userId, userProperties });
  }
};
